import * as _ from "lodash";
import * as toastr from "toastr";
import * as Models from "../../Models";
import { Banner, IBanner } from "../../Models/Banner/Banner";
import * as Services from "../../Services";
import * as Shared from "../../Shared";
import { AlertSeverity } from "../../Models/Banner/AlertSeverity";
import { ThemeConsumer } from "styled-components";
import { Reference } from "../../Shared/Constants/Reference";
import { SystemIncentive } from "../../Models/Customer";

export interface ICustomerScope extends ng.IScope {
  customerCtrl: CustomerCtrl;
}

export class CustomerCtrl {
  public static $inject = [
    "$state",
    "CustomerApiClient",
    "$scope",
    "DocusignApiClient",
    "UserService",
    "InvoiceApiClient",
    "CreditCheckApiClient",
    "BannerApiClient",
    "OpportunityApiClient",
  ];

  public customerDetails: Models.Customer.SolarCustomerDetailsResponse.ISolarCustomerDetailsResponse;
  public additionalAccountDetails: Models.Customer.SolarAdditionalCustomerDetailsResponse.ISolarAdditionalCustomerDetailsResponse;
  public chartProperties: any;
  public igsCustomerNumber: string;
  public invoices: Models.Customer.InvoiceDetailResponse.InvoiceDetailResponse[];
  public loadedAdditionalDetails: boolean;
  public isInactive: boolean;
  public pastCreditCheck: boolean;
  public showContactInfo: boolean = true;
  public canSearchCustomers = false;
  public roleList = new Models.Auth.RoleList.RoleList();
  public banners: Banner[];
  public propertyDetails: Models.Property.PropertyDetailsResponse.IPropertyDetailsResponse;
  private minIncentivesLength: number = 3;
  public bestIdentifier: string;
  public user: Models.UserModel.UserModel;

  constructor(
    public $state: angular.ui.IStateService,
    private customerApiClient: Services.CustomerApiClient.ICustomerApiClient,
    public $scope: ICustomerScope,
    public docusignApiClient: Services.DocusignApiClient.IDocusignApiClient,
    public userService: Services.UserService.IUserService,
    public invoiceApiClient: Services.InvoiceApiClient.IInvoiceApiClient,
    public creditCheckApiClient: Services.CreditCheckApiClient.ICreditCheckApiClient,
    private bannerApiClient: Services.BannerApiClient.IBannerApiClient,
    private opportunityApiClient: Services.OpportunityApiClient.IOpportunityApiClient,
  ) {
    this.$scope.customerCtrl = this;
    this.igsCustomerNumber = this.$state.params["igsCustomerNumber"];

    if (this.igsCustomerNumber === "search") {
      this.$state.go("main.customersearchv2");
      return;
    }

    this.userService.getCurrentUser().then((user: any) => {
      if (user) {
        this.user = user;
        var custNum = this.igsCustomerNumber;
        this.$state.go("main.customerv2", { igsCustomerNumber: custNum });
      }
      this.canSearchCustomers =
        _.includes(
          user.roles.map((r) => r.name.toLowerCase()),
          this.roleList.searchCustomers.toLowerCase(),
        ) || user.groups.find((x) => x.name.endsWith("Sales")) != null;
      this.loadCustomerDetails();
    });

    this.loadedAdditionalDetails = false;

    this.chartProperties = {};
    this.chartProperties.labels = ["July", "Aug", "Sept", "Oct", "Nov", "Dec"];
    this.chartProperties.series = ["Estimated", "Actual"];
    this.chartProperties.data = [
      [550, 522, 461, 401, 332, 260],
      [600, 518, 312, 401, 350, 255],
    ];
    this.chartProperties.options = { legend: { display: true } };
  }

  public openNotes = (): void => {
    document.getElementById("notes").style.width = "100%";
  };

  public getAdditionalCustomerDetails = (): void => {
    if (!this.loadedAdditionalDetails) {
      if (this.bestIdentifier == undefined)
        this.customerDetails.customer.abpAccountNumber != undefined
          ? this.customerDetails.customer.abpAccountNumber
          : this.igsCustomerNumber;
      this.customerApiClient
        .getAdditionalCustomerDetails(
          new Models.Customer.SolarAdditionalCustomerDetailsRequest.SolarAdditionalCustomerDetailsRequest(
            this.bestIdentifier,
          ),
        )
        .then(
          (result: Models.Customer.SolarAdditionalCustomerDetailsResponse.ISolarAdditionalCustomerDetailsResponse) => {
            this.additionalAccountDetails = result;
            if (this.additionalAccountDetails.system != null) {
              this.additionalAccountDetails.system.mountingTypeName = this.getMountingType(result);
            }
            this.loadedAdditionalDetails = true;
            this.setSystemIncentives(result);
          },
        );
      this.customerApiClient
        .getCustomerEvent(this.igsCustomerNumber, Shared.Constants.Reference.Reference.creditCheckEventCode)
        .then((action: Models.Customer.CustomerEvent.ICustomerEvent) => {
          if (action?.completionDate != null) {
            this.pastCreditCheck = true;
          }
        });
    }
  };

  public flipShowContactInfo = (): void => {
    this.showContactInfo = !this.showContactInfo;
  };

  private setSystemIncentives = (
    result: Models.Customer.SolarAdditionalCustomerDetailsResponse.SolarAdditionalCustomerDetailsResponse,
  ) => {
    if (result?.system?.systemIncentives != null) {
      var systemIncentives = result.system.systemIncentives;
      const length = systemIncentives.length;
      const fill = Array.from(
        { length: this.minIncentivesLength },
        (_, i) => new SystemIncentive.SystemIncentive(-1 * (i + 1)),
      );

      this.additionalAccountDetails.system.systemIncentives =
        length < this.minIncentivesLength
          ? this.alignSystemIncentives(fill)
          : this.additionalAccountDetails.system.systemIncentives;
    }
  };

  private getMountingType = (
    customerDetails: Models.Customer.SolarAdditionalCustomerDetailsResponse.ISolarAdditionalCustomerDetailsResponse,
  ) =>
    customerDetails.system.systemComponents.map((sc) => sc.component?.mountingType?.name).find((n) => !!n) || "Unknown";

  private alignSystemIncentives = (fill: Models.Customer.SystemIncentive.SystemIncentive[]) => {
    _.forEach(fill, (si) => {
      var existing = _.find(this.additionalAccountDetails.system.systemIncentives, { identifier: si.identifier });
      if (existing == undefined) {
        this.additionalAccountDetails.system.systemIncentives.push(si);
      }
    });
    return _.orderBy(this.additionalAccountDetails.system.systemIncentives, ["identifier"], ["asc"]);
  };

  private loadCustomerDetails = (): ng.IPromise<void> => {
    return this.customerApiClient
      .getCustomerDetails(
        new Models.Customer.SolarAccountDetailsRequest.SolarAccountDetailsRequest(this.igsCustomerNumber),
      )
      .then(
        (result: Models.Customer.SolarCustomerDetailsResponse.ISolarCustomerDetailsResponse) => {
          this.customerDetails = result;
          if (result.customer.igsCustomerNumber) this.igsCustomerNumber = result.customer.igsCustomerNumber;

          this.phoneNumberFormat(this.customerDetails.primaryPhone);
          this.opportunityNumberFormat(this.customerDetails.customer.partnerCustomerNumber);
          this.initMap();
          this.loadBanners();
          this.isInactive = !result.isActive;
          if (result.customer.communicationType.code == "DNC") this.showContactInfo = false;
          this.opportunityApiClient
            .getLatestPropertyRecord(this.customerDetails.customer.partnerCustomerNumber)
            .then((res) => {
              if (!!res) {
                this.propertyDetails = res;
                this.propertyDetails.propertyOwnerNames = this.propertyDetails.propertyOwners
                  .map((po) => po.fullNameRaw)
                  .join(" / ");
              }
            });
        },
        () => {
          this.$state.go("main.customersearchv2");
          toastr.error("Please try searching for customer", "Customer Not Found");
        },
      );
  };

  private initMap = (): void => {
    var customerAddress = new google.maps.LatLng(
      this.customerDetails.serviceAddress.latitude,
      this.customerDetails.serviceAddress.longitude,
    );
    var map = new google.maps.Map(document.getElementById("map"), {
      zoom: 15,
      center: customerAddress,
    });
    var marker = new google.maps.Marker({
      position: customerAddress,
      map: map,
    });
  };

  private phoneNumberFormat = (primaryPhoneNumber: string): void => {
    var s2 = ("" + primaryPhoneNumber).replace(/\D/g, "");
    var m = s2.match(/^(\d{3})(\d{3})(\d{4})$/);
    this.customerDetails.primaryPhone = !m ? null : "(" + m[1] + ") " + m[2] + "-" + m[3];
  };

  private opportunityNumberFormat = (opportunityNumber: string): void => {
    if (opportunityNumber == null) {
      this.customerDetails.customer.partnerCustomerNumber = "N/A";
    }
  };

  private loadBanners = (): void => {
    this.banners = [];

    this.bannerApiClient.getActiveGeneralBanners().then((generalBanners) => {
      this.banners = generalBanners;

      if (this.bestIdentifier == undefined)
        this.bestIdentifier = this.igsCustomerNumber ?? this.customerDetails.customer.abpAccountNumber;

      this.bannerApiClient.getActiveCustomerBanners(this.bestIdentifier).then((customerBanners) => {
        if (customerBanners != null) this.banners = this.banners.concat(customerBanners);

        if (this.customerDetails) {
          if (this.customerDetails.contractAmendmentNameInProgress) {
            this.addBanner(
              `Contract Amendment-Name in progress. Updated name - ${this.customerDetails.pendingNameForContractAmendment}`,
              Reference.criticalSeverityCode,
            );
          }
          if (this.customerDetails.contractAmendmentAddressInProgress) {
            this.addBanner(
              `Contract Amendment-Address in progress. Updated address - ${this.customerDetails.pendingAddressForContractAmendment}`,
              Reference.criticalSeverityCode,
            );
          }
          if (this.customerDetails.contractAmendmentTrustInProgress) {
            this.addBanner(
              `Contract Amendment-Trust in progress.  Updated Trust Name - ${this.customerDetails.pendingTrustNameForContractAmendment}`,
              Reference.criticalSeverityCode,
            );
          }
          if (this.customerDetails.contractAmendmentNoTrustInProgress) {
            this.addBanner(
              `Contract Amendment-Trust in progress. Updated - no longer in Trust.`,
              Reference.criticalSeverityCode,
            );
          }
          if (this.customerDetails.contractAmendmentRateInProgress) {
            this.addBanner(`Contract Amendment-Rate in progress.`, Reference.criticalSeverityCode);
          }
          if (this.customerDetails.contractAmendmentRequired) {
            this.addBanner(
              `A contract amendment is required. Invoice will be rejected. Please submit a contract amendment to avoid rejection.`,
              Reference.criticalSeverityCode,
            );
          }
          if (this.customerDetails.safeHarborContractMethodDeadline) {
            this.addBanner(
              `This customer currently qualifies for the Contract Method for Safe Harbor until ${this.customerDetails.safeHarborContractMethodDeadline}.`,
              Reference.warningSeverityCode,
            );
          }
          if (this.customerDetails.contractRecisionExpiryDate) {
            this.addBanner(
              `Milestone 1 submission will not be available until ${this.customerDetails.contractRecisionExpiryDate}.`,
              Reference.warningSeverityCode,
            );
          }
        }
      });
    });
  };

  private addBanner(title: string, severityCode: string) {
    const banner = new Banner();
    const severity = new AlertSeverity();
    severity.code = severityCode;

    banner.title = title;
    banner.severity = severity;

    if (!this.banners) {
      this.banners = [];
    }

    this.banners.push(banner);
  }
}
